import classNames from 'classnames'
import { forwardRef, useRef, type CSSProperties, type ReactNode } from 'react'
import { mergeRefs } from 'react-merge-refs'
import { Size } from '@opallabs/gem'
import { Avatar, Icon } from '@opallabs/lapidary'
import { useDraggableCalendarItem } from '../../dnd'
import { withCalendarItemMenu, type CalendarMenuProps } from '../../menu'
import { barColors } from '../../resolveItemColor'
import { CalendarSizes } from '../../sizes'
import { type CalendarItem } from '../../types'
import * as styles from '../styles'
import { type CalendarDensity, type CalendarNamePriority } from '../types'

/**
 * The fewest characters of a name a crowded row leaves readable. Below about
 * this many there is nothing left to recognise the item by, so the space
 * between the names and the trailing details gives way first.
 */
const NAME_FLOOR = 6

/**
 * What a tooltip or a menu hands its trigger, and nothing else: the chip takes
 * these and puts them on its own element, so nothing sits between an
 * absolutely positioned window and the day that places it.
 *
 * They are not a way in for a caller. `onClick` in particular belongs to the
 * trigger; what opens the item is `onItemClick`, which also decides whether the
 * chip is a control or a label.
 */
type TriggerProps = Pick<
  React.HTMLAttributes<HTMLElement>,
  | 'aria-describedby'
  | 'aria-label'
  | 'onBlur'
  | 'onClick'
  | 'onFocus'
  | 'onPointerDown'
  | 'onPointerEnter'
  | 'onPointerLeave'
  | 'onPointerMove'
> &
  Readonly<{ 'data-state'?: string }>

export type ContentChipProps = Readonly<{
  /**
   * The account the content publishes from, for the second line of a card.
   * Absent, the card names the lack rather than leaving the line blank.
   */
  accountName?: string
  /**
   * Overrides the chip's accessible name. The in-market window says what kind
   * of thing it is; a plain chip is named by what it shows.
   */
  ariaLabel?: string
  /** Extra classes for the chip itself, such as the mark that opens a run. */
  className?: string
  color?: string
  /** @default 'row' */
  density?: CalendarDensity
  /**
   * Which ends of a multi-day window this chip is drawn across. A false edge
   * is squared off, so the chip reads as carrying on into the week before or
   * after. Absent means the chip stands on one day and has two real ends.
   *
   * What a squared edge means belongs in `ariaLabel`, beside the days the
   * window runs.
   */
  edges?: { isEnd: boolean; isStart: boolean }
  /** @default false */
  isDimmed?: boolean
  /** @default false */
  isDraggable?: boolean
  /**
   * The chip is drawn across more than one day. Its details sit against its
   * names rather than at its trailing edge, which on a chip that wide would
   * be a day away from them.
   *
   * @default false
   */
  isWide?: boolean
  item: CalendarItem
  /**
   * The mark the chip leads with. `account` draws the account's avatar in
   * place of the channel's mark, for a month sorted into runs of one account.
   *
   * @default 'channel'
   */
  mark?: 'account' | 'channel'
  /** The right-click menu on the chip. Absent means it has none. */
  menu?: CalendarMenuProps
  /**
   * Which of the two names the chip leads with. Only content inside a moment
   * has two.
   *
   * @default 'content'
   */
  namePriority?: CalendarNamePriority
  /**
   * Opens what the chip stands for. A chip with nowhere to send a click is
   * drawn as a label rather than as a control.
   *
   * It is not `onClick`, because a tooltip or a menu hands its trigger an
   * `onClick` of its own and the two have different shapes.
   */
  onItemClick?: (item: CalendarItem) => void
  /**
   * Fired as the pointer or focus lands on the chip, and with nothing as it
   * leaves. A host can start fetching what a click would need.
   */
  onItemHover?: (itemId: string | undefined) => void
  /** The moment this content belongs to, which a drop is measured against. */
  parent?: CalendarItem
  renderChannelIcon?: (item: CalendarItem, variant?: 'card') => ReactNode
  renderStatusIcon?: (item: CalendarItem, size: number) => ReactNode
  /** @default true */
  shouldShowLabelColor?: boolean
  /**
   * Show the content's own name. Off, the chip is named by its moment alone —
   * unless it has no moment, which leaves its own name the only one it has.
   *
   * @default true
   */
  shouldShowContentName?: boolean
  /** @default true */
  shouldShowMomentBadge?: boolean
  /** @default true */
  shouldShowTime?: boolean
}> &
  TriggerProps

/**
 * One piece of content on the day it is scheduled. The bar down its leading edge
 * carries the color it inherits from its parent moment, which is what ties a
 * moment's bar to the content underneath it.
 *
 * At card density the same chip grows a line for the moment it belongs to and
 * another for the account it publishes from, in that order. The title, the
 * channel and the workflow mark are on every density: they are what the design
 * says always shows.
 */
export const ContentChip = forwardRef<HTMLElement, ContentChipProps>(
  (
    {
      accountName,
      ariaLabel,
      className,
      color,
      density = 'row',
      edges,
      isDimmed = false,
      isDraggable = false,
      isWide = false,
      item,
      mark = 'channel',
      menu,
      namePriority = 'content',
      onItemClick,
      onItemHover,
      parent,
      renderChannelIcon,
      renderStatusIcon,
      shouldShowContentName = true,
      shouldShowLabelColor = true,
      shouldShowMomentBadge = true,
      shouldShowTime = true,
      ...triggerProps
    },
    forwardedRef
  ) => {
    const derived = color && shouldShowLabelColor ? barColors(color) : null
    const ref = useRef<HTMLElement>(null)

    useDraggableCalendarItem({
      // The ghost carries the color the chip is actually drawn in, which is
      // nothing at all when a person has turned label colors off.
      color: derived ? color : undefined,
      isEnabled: isDraggable,
      item,
      parent,
      ref,
      renderChannelIcon,
    })

    // The drag hook holds the element and a tooltip wants the same one.
    const chipRef = mergeRefs([ref, forwardedRef])

    const isCard = density !== 'row'
    const lock = item.isPrivate && (
      <Icon ariaLabel="Private" glyph="gem-locked" size={Size.SmallIcon} />
    )
    const time = shouldShowTime && item.time && (
      <span className={styles.selectors.chipTime}>{item.time}</span>
    )
    const statusMark = renderStatusIcon?.(item, CalendarSizes.chipIconSize)

    // The two names a chip can carry, and which of them leads. Either can be
    // turned off; a chip left with neither falls back to the content's own
    // name, because a chip that says nothing about itself is worse than a
    // chip that says one thing too many.
    const momentTitle = shouldShowMomentBadge ? parent?.title : undefined
    const contentTitle = shouldShowContentName ? item.title : undefined
    const isMomentFirst =
      momentTitle !== undefined &&
      (contentTitle === undefined || namePriority === 'moment')
    const [primaryTitle, secondTitle] = isMomentFirst
      ? [momentTitle, contentTitle]
      : [contentTitle ?? item.title, momentTitle]

    // A row draws both names on the one line, packed from the leading edge: the
    // first set the way a card's title is, the second after a separator,
    // quieter. Neither is capped — they shrink together, the first down to a
    // few characters of itself and the second as far as the row needs.
    const rowName = (title: string, isMoment: boolean, isSecond: boolean) => (
      <span
        className={classNames(styles.selectors.chipName, {
          [styles.selectors.chipQuietName]: isSecond,
          // The content's own name is marked wherever it lands, because that is
          // the name the rest of the calendar looks an item up by.
          [styles.selectors.chipTitle]: !isMoment,
        })}
        // A first name no longer than the floor has nothing to protect, so it
        // keeps its own length. A floor in `ch` would hold more than that: a
        // `ch` is the width of a zero, wider than most letters, and the extra
        // sat as a gap before the separator.
        style={
          isSecond
            ? undefined
            : title.length > NAME_FLOOR
              ? { minInlineSize: `${NAME_FLOOR}ch` }
              : { flexShrink: 0 }
        }
      >
        {title}
      </span>
    )
    const renderRowNames = () =>
      secondTitle === undefined ? (
        rowName(primaryTitle, isMomentFirst, false)
      ) : (
        <>
          {rowName(primaryTitle, isMomentFirst, false)}
          <span className={styles.selectors.chipNameSeparator}>·</span>
          {rowName(secondTitle, !isMomentFirst, true)}
        </>
      )

    // A run of one account is what the chips have in common, so the chip leads
    // with the account rather than with the channel: a column of channel marks
    // would say nothing about why these chips are together. An account whose
    // name has not arrived keeps the channel mark, because an avatar with
    // nothing in it says less than the mark it replaced.
    //
    // The mark fills its box where a Gem glyph paints only the middle of its
    // own, so it sits in a span that holds it a little further off the words.
    const leadingMark = (variant?: 'card') => {
      const drawn =
        mark === 'account' && accountName !== undefined ? (
          <Avatar
            fullName={accountName}
            size={
              variant === 'card'
                ? CalendarSizes.cardDetailIconSize
                : CalendarSizes.chipIconSize
            }
          />
        ) : (
          renderChannelIcon?.(item, variant)
        )

      return drawn === undefined || drawn === null ? null : (
        <span className={styles.selectors.chipMark}>{drawn}</span>
      )
    }

    const chipClassName = classNames(styles.selectors.chip, className, {
      [styles.stateVariables.card]: isCard,
      [styles.stateVariables.clippedEnd]: edges !== undefined && !edges.isEnd,
      [styles.stateVariables.clippedStart]:
        edges !== undefined && !edges.isStart,
      [styles.stateVariables.colorless]: !derived,
      [styles.stateVariables.dimmed]: isDimmed,
      [styles.stateVariables.draggable]: isDraggable,
      [styles.stateVariables.wide]: isWide,
    })
    const style = derived
      ? ({
          [styles.cssVariables.itemBorder]: derived.border,
        } as CSSProperties)
      : undefined

    const contents = (
      <>
        {isCard ? (
          <span className={styles.selectors.chipBody}>
            <span
              className={classNames(
                styles.selectors.chipLine,
                styles.selectors.chipTitleLine
              )}
            >
              {lock}
              <span className={styles.selectors.chipTitle}>{primaryTitle}</span>
              {/* The time and the mark stand together at the trailing edge,
                  so the title has the whole of the line to flow into. A card
                  with neither draws no cluster: an empty one would still take
                  a column gap of the title's width. */}
              {(time || statusMark) && (
                <span className={styles.selectors.chipDetails}>
                  {time}
                  {statusMark}
                </span>
              )}
            </span>
            {/* A card has a line of its own for the second name, so the two
                swap lines rather than sharing one. */}
            {secondTitle !== undefined && (
              <span
                className={classNames(
                  styles.selectors.chipLine,
                  styles.selectors.chipMomentLine
                )}
              >
                <span className={styles.selectors.chipMoment}>
                  <Icon
                    ariaMode="hidden"
                    glyph={
                      isMomentFirst
                        ? 'gem-content-outlined'
                        : 'gem-moment-outlined'
                    }
                    size={CalendarSizes.cardMomentIconSize}
                  />
                  {/* Its own element: a bare text run in a flex line takes no
                      ellipsis, so it was cut off at the card's edge. */}
                  <span className={styles.selectors.chipMomentName}>
                    {secondTitle}
                  </span>
                </span>
              </span>
            )}
            <span className={styles.selectors.chipLine}>
              {leadingMark('card')}
              <span className={styles.selectors.chipTitle}>
                {accountName ?? 'No Account'}
              </span>
            </span>
          </span>
        ) : (
          <>
            {leadingMark()}
            {lock}
            {renderRowNames()}
            {/* Everything after the names is held at the trailing edge, so it
                stays in the same place from one row to the next. */}
            {!isWide && (
              <span
                aria-hidden={true}
                className={styles.selectors.chipSpacer}
              />
            )}
            {time}
            {statusMark}
          </>
        )}
      </>
    )

    // A chip with nowhere to send a click is a label, not a control.
    return withCalendarItemMenu(
      menu,
      item,
      onItemClick === undefined ? (
        <span
          {...triggerProps}
          ref={chipRef as React.Ref<HTMLSpanElement>}
          aria-label={ariaLabel ?? triggerProps['aria-label']}
          className={chipClassName}
          style={style}
        >
          {contents}
        </span>
      ) : (
        <button
          {...triggerProps}
          ref={chipRef as React.Ref<HTMLButtonElement>}
          aria-label={ariaLabel ?? triggerProps['aria-label']}
          className={chipClassName}
          style={style}
          type="button"
          // The tooltip's own handlers ride along: the spread above cannot
          // join two handlers of one name, so each is called here by hand.
          onBlur={(event) => {
            triggerProps.onBlur?.(event)
            onItemHover?.(undefined)
          }}
          onClick={(event) => {
            // A tooltip closes itself on a click through an `onClick` of its
            // own, which the spread above cannot join to this one.
            triggerProps.onClick?.(event)
            onItemClick(item)
          }}
          onFocus={(event) => {
            triggerProps.onFocus?.(event)
            onItemHover?.(item.id)
          }}
          onPointerEnter={(event) => {
            triggerProps.onPointerEnter?.(event)
            onItemHover?.(item.id)
          }}
          onPointerLeave={(event) => {
            triggerProps.onPointerLeave?.(event)
            onItemHover?.(undefined)
          }}
        >
          {contents}
        </button>
      )
    )
  }
)
ContentChip.displayName = 'Calendar: ContentChip'
